import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast, { Toaster } from 'react-hot-toast';
import { authApi } from '../api/client';
import { getApiErrorMessage } from '../api/errors';
import { usePageTitle } from '../hooks/usePageTitle';
import { Image } from '../components/common/NextAdapters';
import styles from './LoginPage.module.css';
import { FiMail, FiCheckCircle } from 'react-icons/fi';

export default function ForgotPasswordPage() {
  usePageTitle('Forgot Password · Kast');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Please enter your email'); return; }

    setLoading(true);
    try {
      await authApi.forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Something went wrong. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <Toaster position="top-center" toastOptions={{
        style: { background: 'var(--popover)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px', fontSize: '14px', backdropFilter: 'blur(12px)' },
        error: { iconTheme: { primary: 'var(--destructive)', secondary: '#fff' } },
      }} />
      <div className={styles.bg}>
        <Image src="/bg.png" alt="" fill priority className={styles.bgImg} />
        <div className={styles.bgOverlay} />
        <div className={styles.blob1} />
        <div className={styles.blob2} />
        <div className={styles.blob3} />
      </div>

      <motion.div
        className={styles.card}
        initial={{ opacity: 0, scale: 0.95, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
      >
        <Link to="/" className={styles.logo} aria-label="Go to home">
          <img
            src="/cast-logo-full.png"
            alt="BoldscholarKast"
            className={styles.logoImg}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </Link>

        {sent ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <FiCheckCircle size={40} color="var(--primary)" />
            </div>
            <h1 className={styles.heading}>Check Your Email</h1>
            <p className={styles.sub}>
              If <strong>{email}</strong> is registered, we've sent a link to reset your password.
            </p>
          </>
        ) : (
          <>
            <h1 className={styles.heading}>Forgot Password</h1>
            <p className={styles.sub}>Enter your email and we'll send you a reset link.</p>

            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="email">Email</label>
                <div className={styles.inputWrap}>
                  <FiMail className={styles.inputIcon} />
                  <input
                    id="email"
                    type="email"
                    className={styles.input}
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              <motion.button
                type="submit"
                className={styles.submitBtn}
                disabled={loading}
                whileHover={{ scale: loading ? 1 : 1.02 }}
                whileTap={{ scale: loading ? 1 : 0.98 }}
              >
                {loading ? <span className={styles.spinner} /> : 'Send Reset Link'}
              </motion.button>
            </form>
          </>
        )}

        <div className={styles.divider}><span>or</span></div>
        <Link to="/login" className={styles.browseLink}>Back to Sign In</Link>
      </motion.div>
    </div>
  );
}
